import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '@lib/utils';

// Investor Demo Components (feature flag: VITE_INVESTOR_DEMO)
import { AIChatbotWidget } from '@components/investor-demo/AIChatbotWidget';
const INVESTOR_DEMO_ENABLED = import.meta.env['VITE_INVESTOR_DEMO'] === 'true';

/**
 * AppLayout Component
 * Main application layout with sidebar navigation and header
 * Mobile-optimized with hamburger menu and slide-out sidebar
 */
export function AppLayout(): JSX.Element {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // Close mobile sidebar on window resize to desktop
  useEffect(() => {
    const handleResize = (): void => {
      if (window.innerWidth >= 768) {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileSidebarOpen]);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Navigation - Hidden on mobile by default */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0',
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
          isMobile={isMobileSidebarOpen}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header with mobile hamburger */}
        <Header onMobileMenuToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)} />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>

        {/* Footer - Polished Corporate - Responsive padding */}
        <footer className="border-t border-gray-100 bg-gray-50/50 py-3 sm:py-4">
          <div className="container flex items-center justify-center gap-2 px-4 text-xs sm:text-sm text-gray-400">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            <p className="hidden sm:inline">Finova Financial System v0.1.0</p>
            <p className="sm:hidden">Finova v0.1.0</p>
            <span className="text-gray-300">|</span>
            <span className="text-xs font-medium text-teal-600">AI-Powered</span>
          </div>
        </footer>
      </div>

      {/* AI Chatbot Widget - Investor Demo Only - Hidden on mobile */}
      {INVESTOR_DEMO_ENABLED && (
        <div className="hidden sm:block">
          <AIChatbotWidget />
        </div>
      )}
    </div>
  );
}
